"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function MissionSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add("revealed");});},
      { threshold: 0.1 }
    );
    const els = ref?.current?.querySelectorAll(".reveal-hidden");
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-padding bg-sand">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="reveal-hidden order-2 lg:order-1">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden h-[480px] shadow-luxury">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_151010661-1772226462573.png"
                  alt="Luxury beachfront vacation rental property in Panama City Beach perfectly maintained"
                  fill
                  className="object-cover" />
                
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-4 bg-navy rounded-2xl p-5 shadow-luxury">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Est. 2021</p>
                    <p className="text-white/50 text-xs">Panama City Beach, FL</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="reveal-hidden order-1 lg:order-2">
            <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-4">Our Mission</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy mb-6 leading-tight">
              Hotel Standards,{" "}
              <span className="italic font-light text-gradient-gold">Boutique Attention</span>
            </h2>
            <div className="space-y-5 text-navy/65 leading-relaxed mb-8">
              <p>
                Premier Property MGT was founded with a simple belief: vacation rental owners in Panama City Beach deserve a management partner who treats their property like their own — with meticulous care, proactive communication, and relentless attention to detail.
              </p>
              <p>
                We started with two properties in 2021. Today we manage a growing portfolio across PCB, and every single one receives the same obsessive standard of care. Not because we have to. Because we believe your guests&apos; 5-star experience directly translates to your bottom line.
              </p>
              <p>
                Our team lives and works in Panama City Beach. We know which contractors show up on time, which weeks fill up fastest, and how to position your property to outperform the competition — because this is our home too.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-5 pt-6 border-t border-sand-medium">
              {[
              { value: "3,200+", label: "Guest Stays" }]?.
              map((stat) =>
              <div key={stat?.label} className="text-center">
                  <div className="font-heading text-3xl font-bold text-gold mb-1">{stat?.value}</div>
                  <div className="text-navy/50 text-xs uppercase tracking-wide">{stat?.label}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}